import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiFetch } from './client'

export interface EntitlementValue {
  boolean: boolean | null
  numeric: number | null
}

export interface EntitlementsResponse {
  enabled: boolean
  org_id: string
  snapshot: {
    features: Record<string, EntitlementValue>
    fetched_at: number
  }
}

/**
 * Read the active org's Stripe Entitlements snapshot.
 *
 * Backend returns `enabled: false` and an empty `features` map whenever the
 * `STRIPE_ENTITLEMENTS_ENABLED` flag is off, Stripe is not configured, or the
 * org has not yet been provisioned with a Stripe customer. The UI uses
 * `enabled` to decide whether to render the entitlements view or fall back to
 * the legacy spend-cap edit form.
 */
export function useEntitlements() {
  return useQuery({
    queryKey: ['entitlements', 'me'],
    queryFn: () => apiFetch<EntitlementsResponse>('/api/entitlements/me'),
    staleTime: 30_000,
  })
}

export interface UsageEvent {
  id: string
  org_id: string
  user_id: string | null
  call_id: string | null
  operation_id: string | null
  event_type: string
  price_usd: number
  provider: string | null
  billable: number
  minutes: number | null
  source: string
  created_at: number
}

export interface UsageSummary {
  org: {
    name: string
    status: string
    spend_cap_daily_usd: number
    spend_cap_monthly_usd: number
  }
  period: {
    daily_used_usd: number
    monthly_used_usd: number
    daily_cap_usd: number
    monthly_cap_usd: number
    day_start: number
    month_start: number
  }
  daily_series: { day_start: number; total_usd: number; event_count: number }[]
}

export function useUsageSummary() {
  return useQuery({
    queryKey: ['usage', 'summary'],
    queryFn: () => apiFetch<UsageSummary>('/api/usage/summary'),
    refetchInterval: 30_000,
  })
}

export function useUsageEvents(params: { limit?: number; offset?: number } = {}) {
  const limit = params.limit ?? 100
  const offset = params.offset ?? 0
  return useQuery({
    queryKey: ['usage', 'events', limit, offset],
    queryFn: () =>
      apiFetch<{ events: UsageEvent[]; total: number }>(
        `/api/usage/events?limit=${limit}&offset=${offset}`,
      ),
  })
}

export function useUpdateCaps() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: { spend_cap_daily_usd?: number; spend_cap_monthly_usd?: number }) =>
      apiFetch<{ updated: boolean; applied: Record<string, number>; error?: string }>(
        '/api/usage/caps',
        { method: 'PUT', body: JSON.stringify(body) },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['usage'] })
      qc.invalidateQueries({ queryKey: ['auth', 'me'] })
    },
  })
}
